import type { ChatStreamEvent } from '../api/chatStream'

export type McpUiConfig = {
  mcp_enabled?: boolean
  mcp_use_builtin_kb?: boolean
  mcp_display_label?: string
}

export type AgentStepKind = 'refs' | 'retrieval' | 'reasoning' | 'judge' | 'tool' | 'web'

export type AgentStepStatus = 'running' | 'done' | 'error'

export type AgentPreviewRef = {
  item_id: number
  title?: string
}

export type AgentCurationDoc = {
  item_id?: number
  title: string
  relevance_note?: string
  reason?: string
}

export type AgentCurationSummary = {
  skipped?: boolean
  skipReason?: string
  overviewNote?: string
  answerShape?: string
  candidates?: AgentCurationDoc[]
  related?: AgentCurationDoc[]
  excluded?: AgentCurationDoc[]
}

export type AgentStep = {
  id: string
  kind: AgentStepKind
  label: string
  status: AgentStepStatus
  startedAt: number
  durationMs?: number
  detail?: string
  hitCount?: number
  titles?: string[]
  previewRefs?: AgentPreviewRef[]
  round?: number
  curation?: AgentCurationSummary
}

function upsert(steps: AgentStep[], step: AgentStep): AgentStep[] {
  const i = steps.findIndex((s) => s.id === step.id)
  if (i < 0) return [...steps, step]
  const next = [...steps]
  next[i] = { ...next[i], ...step }
  return next
}

function latestHarnessRound(steps: AgentStep[]): number {
  const rounds = steps.map((s) => s.round).filter((r): r is number => typeof r === 'number' && r > 0)
  return rounds.length ? Math.max(...rounds) : 0
}

function finishRunningJudgeAndReasoning(steps: AgentStep[]): AgentStep[] {
  let next = steps
  for (const s of steps) {
    if (s.status !== 'running') continue
    if (s.kind === 'judge') {
      next = finish(next, s.id)
    } else if (s.kind === 'reasoning') {
      next = finish(next, s.id)
      next = next.map((row) => {
        if (row.id !== s.id) return row
        const dur = agentStepDurationLabel(row)
        return { ...row, label: dur ? `已完成推理${dur}` : '已完成推理' }
      })
    }
  }
  return next
}

function finish(steps: AgentStep[], id: string, patch: Partial<AgentStep> = {}): AgentStep[] {
  const i = steps.findIndex((s) => s.id === id)
  if (i < 0) return steps
  const cur = steps[i]
  const ended = Date.now()
  const next = [...steps]
  next[i] = {
    ...cur,
    ...patch,
    status: patch.status ?? 'done',
    durationMs: patch.durationMs ?? Math.max(0, ended - cur.startedAt),
  }
  return next
}

/** 结束所有仍在 running 的步骤，保证时间线同一时刻只有一个 loading。 */
function finishAllRunning(steps: AgentStep[]): AgentStep[] {
  let next = steps
  for (const s of steps) {
    if (s.status === 'running') {
      next = finish(next, s.id)
    }
  }
  return next
}

/** 深度思考开始前：结束其它 running 步骤，但保留「整理回答」步骤（避免同一标签出现两次）。 */
function finishRunningExceptAnswerStream(steps: AgentStep[]): AgentStep[] {
  let next = steps
  for (const s of steps) {
    if (s.status === 'running' && s.id !== 'answer-stream') {
      next = finish(next, s.id)
    }
  }
  return next
}

/** 正文流式输出期间保持「正在生成回答正文」步骤 running。 */
function finishRunningExceptAnswerWrite(steps: AgentStep[]): AgentStep[] {
  let next = steps
  for (const s of steps) {
    if (s.status === 'running' && s.id !== 'answer-write') {
      next = finish(next, s.id)
    }
  }
  return next
}

function finishAnswerWriteStep(steps: AgentStep[]): AgentStep[] {
  if (!steps.some((s) => s.id === 'answer-write')) return steps
  let next = steps
  const cur = steps.find((s) => s.id === 'answer-write')
  if (cur?.status === 'running') {
    next = finish(next, 'answer-write')
  }
  return next.map((s) => {
    if (s.id !== 'answer-write') return s
    const dur = agentStepDurationLabel(s)
    return { ...s, label: dur ? `回答完成${dur}` : '回答完成', detail: undefined }
  })
}

export function hasRunningAgentStep(steps: AgentStep[]): boolean {
  return steps.some((s) => s.status === 'running')
}

function formatDuration(ms: number): string {
  if (ms < 1000) return `${Math.max(0, Math.round(ms / 100) / 10) || 0}s`
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60_000).toFixed(1)}min`
}

export function agentStepDurationLabel(step: AgentStep): string {
  if (step.status === 'running') return ''
  const ms = step.durationMs ?? 0
  if (ms <= 0) return ''
  return `(用时${formatDuration(ms)})`
}

function retrievalStepLabel(
  round: number,
  pageSize: number | undefined,
  mcp: McpUiConfig | undefined,
  agentSearch?: boolean,
  docCount?: number,
  running?: boolean,
  newHits?: number,
): string {
  if (agentSearch) {
    const newPart =
      !running && typeof newHits === 'number'
        ? newHits > 0
          ? ` · 新增 ${newHits} 条`
          : ' · 无新增'
        : ''
    if (running) {
      return round === 1 ? '知识库检索…' : `知识库检索（第 ${round} 次）…`
    }
    const refPart = docCount != null && docCount > 0 ? `（${docCount} 篇参考资料）` : ''
    return round === 1
      ? `知识库检索${refPart}${newPart}`
      : `知识库检索（第 ${round} 次）${refPart}${newPart}`
  }
  const useMcpStyle = mcp?.mcp_enabled && mcp?.mcp_use_builtin_kb
  const base = (mcp?.mcp_display_label || '').trim() || '调用知识库工具集'
  const sizeHint = pageSize != null ? ` · 每轮 ${pageSize} 条` : ''
  const newPart =
    !running && typeof newHits === 'number'
      ? newHits > 0
        ? ` · 新增 ${newHits} 条`
        : ' · 无新增'
      : ''
  if (useMcpStyle) {
    if (round <= 1) return `${base}（第 1 轮${sizeHint}）${running ? '…' : newPart}`
    return `${base}（第 ${round} 轮 · 扩大范围${sizeHint}）${running ? '…' : newPart}`
  }
  if (round === 1) return running ? '调用知识库检索…' : `调用知识库检索${newPart}`
  return running
    ? `扩大检索范围（第 ${round} 轮）…`
    : `扩大检索范围（第 ${round} 轮${sizeHint}）${newPart}`
}

function buildRetrievalDetail(
  ev: {
    hit_count?: number
    new_hits_this_round?: number
    retrieval_queries?: string[]
    retrieval_query?: string
    es_offset?: number
    round?: number
  },
  agentSearch: boolean,
): string | undefined {
  const detailParts: string[] = []
  const qs = ev.retrieval_queries ?? (ev.retrieval_query ? [ev.retrieval_query] : [])
  if (qs.length) detailParts.push(`检索句：\n${qs.map((q) => `· ${q}`).join('\n')}`)
  if (typeof ev.new_hits_this_round === 'number') {
    detailParts.push(`本轮新增 ${ev.new_hits_this_round} 条切片，累计 ${ev.hit_count ?? 0} 条`)
  } else if (typeof ev.hit_count === 'number') {
    detailParts.push(`累计命中 ${ev.hit_count} 条切片`)
  }
  if (!agentSearch && typeof ev.es_offset === 'number' && ev.es_offset > 0) {
    detailParts.push(`已从 ES 偏移 ${ev.es_offset} 继续翻页（问句未改，仅扩大召回范围）`)
  } else if (!agentSearch && (ev.round ?? 0) > 1) {
    detailParts.push('自纠错多轮：沿用首轮改写后的检索句，扩大切片召回范围')
  }
  return detailParts.length ? detailParts.join('\n\n') : undefined
}

const AGENT_ANSWER_LABEL = '判断意图完成，正在整理结果回答'
const AGENT_ANSWER_WAIT_DETAIL =
  '正在调用大模型整理回答（检索结果已就绪，通常需 30 秒～2 分钟，请稍候）…'

export function formatAnswerWaitDetail(startedAt?: number): string {
  if (!startedAt) return AGENT_ANSWER_WAIT_DETAIL
  const sec = Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
  if (sec < 5) return AGENT_ANSWER_WAIT_DETAIL
  return `${AGENT_ANSWER_WAIT_DETAIL}\n已等待 ${sec} 秒…`
}

function isAgentTimeline(steps: AgentStep[]): boolean {
  return steps.some((s) => s.id.startsWith('agent-round-'))
}

export function applyAgentStepEvent(
  steps: AgentStep[],
  ev: ChatStreamEvent,
  mcp?: McpUiConfig,
): AgentStep[] {
  const now = Date.now()

  if (ev.type === 'query_rewrite') {
    if (ev.skip_reason === 'agent_tool') {
      return steps
    }
    const queries = ev.queries ?? []
    const detailParts: string[] = []
    if (ev.skipped) {
      const reason =
        ev.skip_reason === 'cited_document_title'
          ? '已指定文档名'
          : ev.skip_reason === 'standalone_long_question'
            ? '完整长问句'
            : ev.skip_reason === 'casual_no_retrieval'
              ? '无需检索'
              : ev.skip_reason === 'agent_tool'
                ? 'Agentic 工具检索'
                : ev.skip_reason === 'repeated_user_question'
                  ? '重复问句'
                  : ev.skip_reason || '规则命中'
      detailParts.push(`已跳过 LLM 改写（${reason}）`)
    }
    if (ev.context_summary?.trim()) detailParts.push(`主题：${ev.context_summary.trim()}`)
    if (ev.intent?.trim()) detailParts.push(ev.intent.trim())
    if (queries.length) detailParts.push(queries.map((q) => `· ${q}`).join('\n'))
    return upsert(steps, {
      id: 'query-rewrite',
      kind: 'tool',
      label: ev.skipped ? '整理检索句（规则）' : '理解问题并改写检索句',
      status: 'done',
      startedAt: now,
      durationMs: 0,
      detail: detailParts.join('\n') || undefined,
    })
  }

  if (ev.type === 'agent_step_start') {
    return upsert(steps, {
      id: `agent-round-${ev.step}`,
      kind: 'tool',
      label: `Agentic 意图分析（第 ${ev.step}/${ev.max_steps} 步）…`,
      status: 'running',
      startedAt: now,
      round: ev.step,
      detail: '分析用户意图，拟定检索策略…',
    })
  }

  if (ev.type === 'agent_reasoning_start') {
    const rid = `agent-round-${ev.step}`
    const i = steps.findIndex((s) => s.id === rid)
    if (i < 0) {
      return upsert(steps, {
        id: rid,
        kind: 'tool',
        label: 'Agentic 意图分析中…',
        status: 'running',
        startedAt: now,
        round: ev.step,
        detail: '',
      })
    }
    const next = [...steps]
    next[i] = { ...next[i], label: 'Agentic 意图分析中…', detail: '' }
    return next
  }

  if (ev.type === 'agent_reasoning_delta') {
    const rid = `agent-round-${ev.step}`
    const i = steps.findIndex((s) => s.id === rid)
    if (i < 0) {
      return upsert(steps, {
        id: rid,
        kind: 'tool',
        label: 'Agentic 意图分析中…',
        status: 'running',
        startedAt: now,
        round: ev.step,
        detail: ev.content,
      })
    }
    const next = [...steps]
    const cur = next[i]
    next[i] = { ...cur, detail: (cur.detail || '') + ev.content }
    return next
  }

  if (ev.type === 'agent_reasoning_end') {
    const rid = `agent-round-${ev.step}`
    let next = finish(steps, rid)
    next = next.map((s) => {
      if (s.id !== rid) return s
      const dur = agentStepDurationLabel(s)
      return { ...s, label: dur ? `意图分析完成${dur}` : '意图分析完成' }
    })
    return next
  }

  if (ev.type === 'agent_tool_call') {
    const rid = `agent-round-${ev.step}`
    const planDur =
      ev.plan_ms != null && ev.plan_ms > 0 ? `(用时${formatDuration(ev.plan_ms)})` : ''
    const detailParts: string[] = []
    if (ev.reasoning?.trim()) detailParts.push(ev.reasoning.trim())
    if (ev.tool === 'search_knowledge_base') {
      const qs = (ev.arguments?.queries as string[] | undefined) ?? []
      if (qs.length) detailParts.push(qs.map((q) => `· ${q}`).join('\n'))
    }
    if (ev.tool === 'invoke_agent') {
      const code = String(ev.arguments?.agent_code ?? '')
      const task = String(ev.arguments?.task ?? '')
      if (code) detailParts.push(`子智能体：${code}`)
      if (task) detailParts.push(task)
    }
    let next = finish(steps, rid, {
      durationMs: ev.plan_ms != null && ev.plan_ms > 0 ? ev.plan_ms : undefined,
    })
    next = next.map((s) => {
      if (s.id !== rid) return s
      return {
        ...s,
        label: planDur ? `意图分析（第 ${ev.step} 步）${planDur}` : `意图分析（第 ${ev.step} 步）`,
        detail: detailParts.join('\n') || s.detail,
      }
    })
    if (ev.tool === 'finish') {
      return upsert(next, {
        id: 'answer-stream',
        kind: 'judge',
        label: AGENT_ANSWER_LABEL,
        status: 'running',
        startedAt: now,
        round: ev.step,
        detail: AGENT_ANSWER_WAIT_DETAIL,
      })
    }
    return next
  }

  if (ev.type === 'agent_sub_invoke') {
    const detailParts: string[] = []
    if (ev.task?.trim()) detailParts.push(ev.task.trim())
    if (ev.result_preview?.trim()) detailParts.push(ev.result_preview.trim())
    const name = ev.agent_name?.trim() || ev.agent_code?.trim() || '子智能体'
    return upsert(steps, {
      id: `sub-agent-${ev.step}-${ev.agent_code ?? 'unknown'}`,
      kind: 'tool',
      label: `调用子智能体 · ${name}`,
      status: 'done',
      startedAt: now,
      durationMs: 0,
      round: ev.step,
      detail: detailParts.join('\n') || undefined,
    })
  }

  if (ev.type === 'hit_curation_start') {
    const candidates = (ev.candidate_docs ?? []).map((d) => ({
      item_id: d.item_id,
      title: d.title,
    }))
    const count = ev.candidate_count ?? candidates.length
    const next = finish(steps, `retrieval-${ev.round}`)
    return upsert(next, {
      id: 'hit-curation',
      kind: 'tool',
      label: `整理相关文档清单（${count} 篇候选）…`,
      status: 'running',
      startedAt: now,
      round: ev.round,
      curation: { candidates },
      detail:
        count > 0
          ? `正在从 ${count} 篇检索命中文档中筛选与问题直接相关的内容…\n${candidates
              .map((d) => `· ${d.title}`)
              .join('\n')}`
          : '正在整理相关文档清单…',
    })
  }

  if (ev.type === 'hit_curation_done') {
    const related = ev.related_docs ?? []
    const excluded = ev.excluded_docs ?? []
    const relatedRows: AgentCurationDoc[] = related.map((d) => ({
      item_id: d.item_id,
      title: d.title,
      relevance_note: d.relevance_note,
    }))
    const excludedRows: AgentCurationDoc[] = excluded.map((d) => ({
      item_id: d.item_id,
      title: d.title,
      reason: d.reason,
    }))
    let next = finish(steps, 'hit-curation', {
      hitCount: related.length,
      titles: related.map((d) => d.title),
    })
    next = next.map((s) => {
      if (s.id !== 'hit-curation') return s
      const dur = agentStepDurationLabel(s)
      const skipped = Boolean(ev.skipped)
      let doneLabel = '已完成文档整理'
      if (!skipped && related.length) {
        doneLabel =
          excluded.length > 0
            ? `已整理：保留 ${related.length} 篇，排除 ${excluded.length} 篇偏题`
            : `已整理 ${related.length} 篇相关文档`
      } else if (skipped) {
        doneLabel =
          ev.skip_reason === 'parse_failed'
            ? '文档整理未完成（解析失败，已使用全部检索结果）'
            : '已跳过文档整理'
      }
      return {
        ...s,
        label: dur ? `${doneLabel}${dur}` : doneLabel,
        detail: undefined,
        curation: {
          skipped,
          skipReason: ev.skip_reason,
          overviewNote: ev.overview_note,
          answerShape: ev.answer_shape,
          candidates: s.curation?.candidates,
          related: skipped ? [] : relatedRows,
          excluded: excludedRows,
        },
      }
    })
    return next
  }

  if (ev.type === 'entity_scope') {
    const matched = ev.matched_entities ?? []
    const itemIds = ev.item_ids ?? []
    const actionLabels: Record<string, string> = {
      disabled: '图谱增强未开启',
      no_graph: '图谱：无已审核数据',
      no_match: '图谱：未命中实体',
      filter: '图谱：限定检索范围',
      boost: '图谱：命中实体加权',
    }
    const label = actionLabels[ev.action || ''] || '图谱实体匹配'
    const detailParts: string[] = []
    if (ev.note?.trim()) detailParts.push(ev.note.trim())
    if (typeof ev.graph_items_count === 'number') {
      detailParts.push(`已审核图谱 ${ev.graph_items_count} 篇 · 实体池 ${ev.entity_pool_size ?? '—'} 个`)
    }
    if (matched.length) detailParts.push(`命中：${matched.join('、')}`)
    if (itemIds.length) detailParts.push(`关联 item_id：${itemIds.join(', ')}`)
    if (ev.filter_locked) detailParts.push('检索已限定在上述文档内')
    return upsert(steps, {
      id: 'entity-scope',
      kind: 'tool',
      label,
      status: 'done',
      startedAt: now,
      durationMs: 0,
      detail: detailParts.join('\n') || undefined,
    })
  }

  if (ev.type === 'harness_round_start') {
    const pageSize =
      typeof (ev as { page_size?: number }).page_size === 'number'
        ? (ev as { page_size: number }).page_size
        : ev.retrieval_top_k
    const agentSearch = Boolean((ev as { agent_search?: boolean }).agent_search)
    const next = finishAllRunning(steps)
    return upsert(next, {
      id: `retrieval-${ev.round}`,
      kind: 'retrieval',
      label: retrievalStepLabel(ev.round, pageSize, mcp, agentSearch, undefined, true),
      status: 'running',
      startedAt: now,
      round: ev.round,
    })
  }

  if (ev.type === 'harness_round_retrieval') {
    const pageSize =
      typeof (ev as { page_size?: number }).page_size === 'number'
        ? (ev as { page_size: number }).page_size
        : ev.retrieval_top_k
    const agentSearch = Boolean((ev as { agent_search?: boolean }).agent_search)
    const mergedTitles =
      (ev as { merged_titles?: string[] }).merged_titles ?? ev.titles ?? []
    const previewRefs = (ev as { preview_refs?: AgentPreviewRef[] }).preview_refs ?? []
    const docCount = previewRefs.length || mergedTitles.length
    const newHits = (ev as { new_hits_this_round?: number }).new_hits_this_round
    const detail = buildRetrievalDetail(
      {
        hit_count: ev.hit_count,
        new_hits_this_round: (ev as { new_hits_this_round?: number }).new_hits_this_round,
        retrieval_queries: (ev as { retrieval_queries?: string[] }).retrieval_queries,
        retrieval_query: ev.retrieval_query,
        es_offset: (ev as { es_offset?: number }).es_offset,
        round: ev.round,
      },
      agentSearch,
    )
    const entityScope = (ev as { entity_scope?: { action?: string; note?: string; matched_entities?: string[]; item_ids?: number[] } }).entity_scope
    let next = finish(steps, `retrieval-${ev.round}`, {
      label: retrievalStepLabel(ev.round, pageSize, mcp, agentSearch, docCount, false, newHits),
      hitCount: ev.hit_count,
      titles: mergedTitles,
      previewRefs: agentSearch && docCount > 0 ? previewRefs : undefined,
      detail,
    })
    if (!agentSearch && docCount > 0 && ev.round === 1) {
      next = upsert(next, {
        id: 'refs-summary',
        kind: 'refs',
        label: `${docCount} 篇参考资料`,
        status: 'done',
        startedAt: now,
        durationMs: 0,
        hitCount: ev.hit_count,
        titles: mergedTitles,
        previewRefs,
      })
    }
    if (entityScope && ev.round === 1) {
      const matched = entityScope.matched_entities ?? []
      const itemIds = entityScope.item_ids ?? []
      const actionLabels: Record<string, string> = {
        filter: '图谱限定检索',
        boost: '图谱加权命中',
        no_match: '图谱未命中',
        no_graph: '图谱无数据',
        disabled: '图谱未启用',
      }
      const parts: string[] = []
      if (entityScope.note?.trim()) parts.push(entityScope.note.trim())
      if (matched.length) parts.push(`命中：${matched.join('、')}`)
      if (itemIds.length) parts.push(`item_id：${itemIds.join(', ')}`)
      next = upsert(next, {
        id: 'entity-scope',
        kind: 'tool',
        label: actionLabels[entityScope.action || ''] || '图谱实体匹配',
        status: 'done',
        startedAt: now,
        durationMs: 0,
        detail: parts.join('\n') || undefined,
      })
    }
    return next
  }

  if (ev.type === 'harness_think_start') {
    const next = finishAllRunning(steps)
    return upsert(next, {
      id: `reasoning-${ev.round}`,
      kind: 'reasoning',
      label: '推理中…',
      status: 'running',
      startedAt: now,
      round: ev.round,
      detail: '',
    })
  }

  if (ev.type === 'thinking_start') {
    if (steps.some((s) => s.kind === 'reasoning' && s.status === 'running')) return steps
    const next = finishRunningExceptAnswerStream(steps)
    const answerPrep = ev.purpose === 'answer_prep' || next.some((s) => s.id === 'answer-stream')
    let result = next
    if (answerPrep) {
      const ans = result.find((s) => s.id === 'answer-stream')
      if (ans?.status === 'running') {
        result = finish(result, 'answer-stream', {
          label: '判断意图完成',
          detail: undefined,
        })
      }
    }
    return upsert(result, {
      id: answerPrep ? 'answer-prep' : 'reasoning-final',
      kind: 'reasoning',
      label: answerPrep ? '整理回答中…' : '推理中…',
      status: 'running',
      startedAt: now,
      detail: answerPrep ? '' : '',
    })
  }

  if (ev.type === 'thinking_delta' || ev.type === 'thinking') {
    const piece = ev.content
    const rid =
      [...steps].reverse().find((s) => s.kind === 'reasoning' && s.status === 'running')?.id ??
      (steps.some((s) => s.id === 'answer-stream') ? 'answer-prep' : 'reasoning-final')
    const i = steps.findIndex((s) => s.id === rid)
    if (i < 0) {
      return upsert(steps, {
        id: rid,
        kind: 'reasoning',
        label: '推理中…',
        status: 'running',
        startedAt: now,
        detail: piece,
      })
    }
    const next = [...steps]
    const cur = next[i]
    next[i] = { ...cur, detail: (cur.detail || '') + piece }
    return next
  }

  if (ev.type === 'thinking_end' || ev.type === 'harness_think_end') {
    const round = ev.type === 'harness_think_end' ? ev.round : undefined
    const id =
      round != null
        ? `reasoning-${round}`
        : ([...steps].reverse().find((s) => s.kind === 'reasoning' && s.status === 'running')?.id ??
          'reasoning-final')
    let next = finish(steps, id)
    next = next.map((s) => {
      if (s.id !== id) return s
      const dur = agentStepDurationLabel(s)
      const doneLabel =
        s.id === 'answer-prep'
          ? dur
            ? `整理思路完成${dur}`
            : '整理思路完成'
          : dur
            ? `已完成推理${dur}`
            : '已完成推理'
      return { ...s, label: doneLabel }
    })
    // 流式深度思考结束 → 进入整理回答；answer-prep 结束 → 进入正文生成
    if (ev.type === 'thinking_end') {
      if (id === 'answer-prep') {
        return upsert(next, {
          id: 'answer-write',
          kind: 'judge',
          label: '正在生成回答正文…',
          status: 'running',
          startedAt: now,
          detail: '模型正在输出最终回答…',
        })
      }
      if (!next.some((s) => s.kind === 'judge' && s.status === 'running')) {
        const agentMode = isAgentTimeline(next)
        const ansRound =
          next.find((s) => s.id === id)?.round ?? latestHarnessRound(next)
        const existingAnswer = next.find((s) => s.id === 'answer-stream')
        if (agentMode && existingAnswer) {
          next = upsert(next, {
            id: 'answer-stream',
            kind: 'judge',
            label: AGENT_ANSWER_LABEL,
            status: 'running',
            startedAt: existingAnswer.startedAt ?? now,
            detail: AGENT_ANSWER_WAIT_DETAIL,
            round: existingAnswer.round ?? (ansRound > 0 ? ansRound : undefined),
          })
        } else {
          const judgeId = ansRound > 0 ? `judge-${ansRound}` : 'answer-stream'
          next = upsert(next, {
            id: judgeId,
            kind: 'judge',
            label: agentMode ? AGENT_ANSWER_LABEL : '整理检索结果并回答',
            status: 'running',
            startedAt: now,
            round: ansRound > 0 ? ansRound : undefined,
          })
        }
      }
    }
    return next
  }

  if (ev.type === 'harness_judge_start') {
    let next = finishAllRunning(steps)
    const agentMode = isAgentTimeline(next)
    if (ev.mode === 'stream_answer') {
      const probeId = `judge-${ev.round}`
      if (next.some((s) => s.id === probeId && s.status === 'running')) {
        next = finish(next, probeId)
      }
      if (next.some((s) => s.id === 'answer-stream')) {
        return upsert(next, {
          id: 'answer-stream',
          kind: 'judge',
          label: agentMode ? AGENT_ANSWER_LABEL : '整理检索结果并回答',
          status: 'running',
          startedAt: now,
          round: ev.round,
          detail: AGENT_ANSWER_WAIT_DETAIL,
        })
      }
      return upsert(next, {
        id: agentMode ? 'answer-stream' : `answer-${ev.round}`,
        kind: 'judge',
        label: agentMode ? AGENT_ANSWER_LABEL : '整理检索结果并回答',
        status: 'running',
        startedAt: now,
        round: ev.round,
        detail: AGENT_ANSWER_WAIT_DETAIL,
      })
    }
    return upsert(next, {
      id: `judge-${ev.round}`,
      kind: 'judge',
      label: '判断是否找到足够依据',
      status: 'running',
      startedAt: now,
      round: ev.round,
      detail: '正在阅读检索片段，判断是否足以回答您的问题…',
    })
  }

  if (ev.type === 'answer_generation_start' || ev.type === 'answer_generation_progress') {
    const hitHint =
      ev.type === 'answer_generation_start' && typeof ev.hit_count === 'number'
        ? `\n已检索 ${ev.hit_count} 条相关片段，正在生成回答…`
        : ''
    const phaseDetail =
      ev.type === 'answer_generation_progress'
        ? ev.phase === 'llm_thinking'
          ? undefined
          : ev.phase === 'llm_answer'
            ? '正在生成回答正文…'
            : ev.phase === 'llm_stream'
              ? '已连接大模型，等待首个输出…'
              : undefined
        : undefined
    const writeStep = steps.some((s) => s.id === 'answer-write')
    const prepRunning = steps.some((s) => s.id === 'answer-prep' && s.status === 'running')
    if (prepRunning && ev.type === 'answer_generation_progress') {
      return steps
    }
    return upsert(steps, {
      id: writeStep ? 'answer-write' : 'answer-stream',
      kind: 'judge',
      label:
        writeStep ||
        (ev.type === 'answer_generation_progress' && ev.phase === 'llm_answer')
          ? '正在生成回答正文…'
          : AGENT_ANSWER_LABEL,
      status: 'running',
      startedAt:
        steps.find((s) => s.id === (writeStep ? 'answer-write' : 'answer-stream'))?.startedAt ?? now,
      detail: phaseDetail ?? (writeStep ? '模型正在输出最终回答…' : `${AGENT_ANSWER_WAIT_DETAIL}${hitHint}`),
    })
  }

  if (ev.type === 'delta') {
    return finishRunningExceptAnswerWrite(steps)
  }

  if (ev.type === 'rag_done') {
    return finishAnswerWriteStep(finishRunningJudgeAndReasoning(steps))
  }

  if (ev.type === 'harness_round_result') {
    let next = finish(steps, `judge-${ev.round}`)
    next = finish(next, `answer-${ev.round}`)
    next = finish(next, 'answer-stream')
    next = finish(next, 'answer-write')
    if (!ev.found && ev.action?.includes('retry')) {
      const retryLabel =
        mcp?.mcp_enabled && mcp?.mcp_use_builtin_kb
          ? `继续${(mcp.mcp_display_label || '').trim() || '调用工具集'}`
          : '继续检索知识库'
      next = upsert(next, {
        id: `tool-retry-${ev.round}`,
        kind: 'tool',
        label: retryLabel,
        status: 'done',
        startedAt: now,
        durationMs: 0,
        round: ev.round,
      })
    }
    return next
  }

  return steps
}

export function shouldShowAgentIntro(steps: AgentStep[]): boolean {
  return steps.some(
    (s) =>
      (s.kind === 'refs' && s.status === 'done') ||
      (s.kind === 'retrieval' &&
        s.status === 'done' &&
        ((s.previewRefs?.length ?? 0) > 0 || (s.titles?.length ?? 0) > 0)),
  )
}
